import { Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Checkbox, Textarea } from '@creanizer/ui'
import type { SocietyInput } from '@creanizer/types'

interface TemplateModeFormProps {
  input: SocietyInput
  onChange: (input: SocietyInput) => void
}

const TEMPLATES = {
  'thieves-guild': {
    name: '盗賊ギルド',
    description: '都市の影で暗躍する盗賊たちの組織',
    modifiers: ['地下組織', '情報網', '縄張り争い', '掟と制裁']
  },
  'mage-academy': {
    name: '魔法学院',
    description: '魔術師を育成する権威ある教育機関',
    modifiers: ['派閥争い', '古代の知識', '危険な実験', '秘密の図書館']
  },
  'merchant-guild': {
    name: '商人ギルド',
    description: '交易と商業を支配する強力な経済組織',
    modifiers: ['交易路', '独占権', '政治的影響力', '密輸']
  },
  'secret-cult': {
    name: '秘密教団',
    description: '禁断の神や理念を崇拝する地下組織',
    modifiers: ['洗脳', '生贄', '終末思想', '異端審問']
  },
  'knight-order': {
    name: '騎士団',
    description: '名誉と正義を重んじる武装組織',
    modifiers: ['聖戦', '誓約', '堕落', '伝説の武具']
  },
  'assassin-guild': {
    name: '暗殺者ギルド',
    description: '死の契約を請け負う影の組織',
    modifiers: ['暗殺術', '毒物', '変装', '血の契約']
  },
  'rebel-faction': {
    name: '反乱軍',
    description: '現体制に抵抗する革命組織',
    modifiers: ['地下活動', 'プロパガンダ', 'ゲリラ戦', '民衆の支持']
  },
  'crime-syndicate': {
    name: '犯罪シンジケート',
    description: '組織犯罪を統括する巨大な闇組織',
    modifiers: ['縄張り', '賄賂', '暴力', '裏取引']
  },
  'explorer-guild': {
    name: '探検家ギルド',
    description: '未知の地を探索する冒険者たちの組織',
    modifiers: ['遺跡探索', '地図作成', '珍しい発見', '危険な探検']
  },
  'mercenary-company': {
    name: '傭兵団',
    description: '金で雇われる職業軍人の集団',
    modifiers: ['戦争請負', '裏切り', '名誉なき戦い', '高額報酬']
  },
  'friends-group': {
    name: '仲間達',
    description: '共通の目的や絆で結ばれた友人グループ',
    modifiers: ['友情', '協力', '成長', '約束']
  },
  'idol-group': {
    name: 'アイドルグループ',
    description: 'エンターテインメントで人々を魅了するパフォーマー集団',
    modifiers: ['人気', 'ライバル', 'スキャンダル', 'ファンとの絆']
  }
}

export function TemplateModeForm({ input, onChange }: TemplateModeFormProps) {
  const handleTemplateChange = (templateType: string) => {
    onChange({
      ...input,
      templateType,
      templateModifiers: TEMPLATES[templateType as keyof typeof TEMPLATES]?.modifiers || []
    })
  }

  const handleModifierToggle = (modifier: string) => {
    const currentModifiers = input.templateModifiers || []
    const newModifiers = currentModifiers.includes(modifier)
      ? currentModifiers.filter(m => m !== modifier)
      : [...currentModifiers, modifier]
    
    onChange({
      ...input,
      templateModifiers: newModifiers
    })
  }

  const selectedTemplate = input.templateType ? TEMPLATES[input.templateType as keyof typeof TEMPLATES] : null

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="templateType">テンプレート選択</Label>
        <Select value={input.templateType} onValueChange={handleTemplateChange}>
          <SelectTrigger id="templateType">
            <SelectValue placeholder="テンプレートを選択してください" />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(TEMPLATES).map(([key, template]) => (
              <SelectItem key={key} value={key}>
                {template.name} - {template.description}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedTemplate && (
        <>
          <div className="p-4 bg-muted rounded-lg">
            <h4 className="font-semibold mb-2">{selectedTemplate.name}</h4>
            <p className="text-sm text-muted-foreground">{selectedTemplate.description}</p>
          </div>

          <div className="space-y-2">
            <Label>テンプレート要素（選択してカスタマイズ）</Label>
            <div className="space-y-2">
              {selectedTemplate.modifiers.map((modifier) => (
                <div key={modifier} className="flex items-center space-x-2">
                  <Checkbox
                    id={modifier}
                    checked={input.templateModifiers?.includes(modifier) ?? true}
                    onCheckedChange={() => handleModifierToggle(modifier)}
                  />
                  <Label htmlFor={modifier} className="font-normal cursor-pointer">
                    {modifier}
                  </Label>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <div className="space-y-2">
        <Label htmlFor="genre">ジャンル設定</Label>
        <Select value={input.genre} onValueChange={(value) => onChange({ ...input, genre: value as any })}>
          <SelectTrigger id="genre">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="fantasy">ファンタジー</SelectItem>
            <SelectItem value="sf">SF</SelectItem>
            <SelectItem value="modern">現代</SelectItem>
            <SelectItem value="post_apocalypse">ポストアポカリプス</SelectItem>
            <SelectItem value="cyberpunk">サイバーパンク</SelectItem>
            <SelectItem value="steampunk">スチームパンク</SelectItem>
            <SelectItem value="mythology">神話</SelectItem>
            <SelectItem value="horror">ホラー</SelectItem>
            <SelectItem value="urban_fantasy">アーバンファンタジー</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="scale">組織規模</Label>
        <Select value={input.scale} onValueChange={(value) => onChange({ ...input, scale: value as any })}>
          <SelectTrigger id="scale">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="small">小規模（10人未満）</SelectItem>
            <SelectItem value="medium">中規模（10-100人）</SelectItem>
            <SelectItem value="large">大規模（100-1000人）</SelectItem>
            <SelectItem value="massive">巨大（1000人以上）</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="additionalSettings">追加設定（省略可）</Label>
        <Textarea
          id="additionalSettings"
          value={input.additionalSettings || ''}
          onChange={(e) => onChange({ ...input, additionalSettings: e.target.value })}
          placeholder="その他の詳細な設定や要望があれば入力してください"
          rows={3}
        />
      </div>
    </div>
  )
}